/**
 * Schema-validated parsing of accepted query parameters (spec §6.1).
 *
 * Any unknown or invalid value is ignored, never thrown at runtime. The portal
 * never trusts these for authorization — `client`/`return_route` are passed to
 * the backend verbatim, and the backend is authoritative (spec §2.7).
 */
import { z } from 'zod';

/** A safe, app-relative return route: starts with a single "/", no "//", no scheme. */
const returnRouteSchema = z
  .string()
  .trim()
  .regex(/^\/(?!\/)[\w\-./?=&%#]*$/, 'must be an app-relative path');

/** Opaque auth-client identifier; conservative charset, capped length. */
const clientSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9_-]{1,64}$/, 'must be an opaque client id');

export interface LoginParams {
  client: string | null;
  returnRoute: string | null;
}

export function parseLoginParams(search: URLSearchParams): LoginParams {
  const client = clientSchema.safeParse(search.get('client') ?? undefined);
  const returnRoute = returnRouteSchema.safeParse(search.get('return_route') ?? undefined);
  return {
    client: client.success ? client.data : null,
    returnRoute: returnRoute.success ? returnRoute.data : null,
  };
}

const intentSchema = z.enum(['login', 'link']);
const providerLabelSchema = z.enum(['google', 'linkedin', 'facebook']);

export interface AuthCompleteParams {
  intent: 'login' | 'link';
  /** UX label only — never a source of truth (spec §6.1). */
  provider: 'google' | 'linkedin' | 'facebook' | null;
}

export function parseAuthCompleteParams(search: URLSearchParams): AuthCompleteParams {
  const intent = intentSchema.safeParse(search.get('intent') ?? undefined);
  const provider = providerLabelSchema.safeParse(search.get('provider') ?? undefined);
  return {
    intent: intent.success ? intent.data : 'login',
    provider: provider.success ? provider.data : null,
  };
}

export function parseErrorCode(search: URLSearchParams): string | null {
  const raw = (search.get('code') ?? '').trim();
  // Allow-list shape only; the catalog decides the message. Never render raw.
  return /^[A-Z0-9_]{1,64}$/.test(raw) ? raw : null;
}
